import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ZigButton,
  ZigInputAmount,
  ZigModalForm,
  ZigSelect,
  ZigTypography,
} from '@ui/index';
import { Box } from '@mui/material';
import { appendToUrlHash } from '../../../../../../util/vwo';
import {
  usePaybisDepositMutation,
  usePaybisPaymentMethodsQuery,
  usePaybisQuoteMutation,
  useUserIpInfoQuery,
} from '../../../../../../apis/user/api';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MIN_INVESTMENT_FIREBLOCKS } from '../../../../../../util/constants';
import { sumsubQuoteValidation } from '../../validations';
import { useActiveExchange } from '../../../../../../apis/user/use';
import { useOpenLink } from '../../../../../../util/hooks/useOpenLink';
import { usePaybisCurrencyOptions, usePaybisMethodOptions } from './use';
import { PaybisQuote } from '../../../../../../apis/user/types';
import PaybisRequestStatus from './PaybisRequestStatusForm';
import { useDebounce } from 'react-use';
import { currencyTo, PAYBIS_REFETCH_QUOTE_DEBOUNCE } from './constants';

function PaybisBuyForm(): React.ReactElement {
  const { t } = useTranslation(['deposit-crypto']);
  const { data: ipInfo, isLoading: isLoadingIpInfo } = useUserIpInfoQuery();
  const exchange = useActiveExchange();

  // we do need a separate state var because we want to reset a quote
  // whenever the user changes smth
  const [activeQuote, setActiveQuote] = useState<PaybisQuote>(null);
  const [lastLoadedQuote, setLastLoadedQuote] = useState<PaybisQuote>(null);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);

  const [getPaybisQuote, { isLoading: isLoadingQuote }] =
    usePaybisQuoteMutation();

  const [triggerPaybisDeposit, { isLoading: isLoadingDeposit }] =
    usePaybisDepositMutation();

  const openPaybisLink = useOpenLink({
    title: t('buy-crypto-modal.paybis-redirect.title'),
    description: t('buy-crypto-modal.paybis-redirect.description'),
    okLabel: t('buy-crypto-modal.paybis-redirect.cta'),
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm<{ amount: string; currency: string; method: string }>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      currency: ipInfo?.currency?.code || 'USD',
    },
    resolver: yupResolver(sumsubQuoteValidation),
  });

  const submitPaymentRequest = useCallback(() => {
    triggerPaybisDeposit({
      userId: exchange.internalId,
      userIp: ipInfo.ip,
      quoteId: activeQuote.quoteId,
      currencyCodeTo: activeQuote.currencyCodeTo,
    })
      .unwrap()
      .then((d) => {
        setActiveRequestId(d.requestId);
        openPaybisLink(d.widgetUrl);
        appendToUrlHash('thanks_buy');
      });
  }, [exchange, ipInfo, activeQuote, openPaybisLink, triggerPaybisDeposit]);

  const fetchNewQuote = useCallback((payload) => {
    getPaybisQuote(payload)
      .unwrap()
      .then((q) => {
        setActiveQuote(q);
        setLastLoadedQuote(q);
        setTimeout(
          () => setActiveQuote(null),
          +new Date(q.expirationDate) - Date.now() + 300,
        );
      });
  }, []);

  const { data: paybisMethods } = usePaybisPaymentMethodsQuery({
    currencyFrom: watch('currency'),
    currencyTo: currencyTo,
  });

  const currencyOptions = usePaybisCurrencyOptions();
  const methodOptions = usePaybisMethodOptions(paybisMethods?.paymentMethods);

  console.error({ paybisMethods });

  useEffect(() => {
    if (!currencyOptions.some((x) => x.value === watch('currency'))) {
      setValue('currency', '');
    }
  }, [currencyOptions]);

  const isQuoteActionable =
    lastLoadedQuote && +new Date(lastLoadedQuote.expirationDate) > Date.now();

  useEffect(() => {
    // hack to reset the form
    setActiveQuote(null);
  }, [watch('method'), watch('amount'), watch('currency')]);

  // reload form once the user changes smth
  useDebounce(
    () => {
      if (isValid && lastLoadedQuote) {
        fetchNewQuote(watch());
      }
    },
    PAYBIS_REFETCH_QUOTE_DEBOUNCE,
    [
      isValid,
      watch('method'),
      watch('amount'),
      watch('currency'),
      fetchNewQuote,
    ],
  );

  if (activeRequestId) {
    return (
      <PaybisRequestStatus
        requestId={activeRequestId}
        reset={() => {
          setActiveRequestId(null);
          setActiveQuote(null);
        }}
      />
    );
  }

  return (
    <>
      <ZigModalForm onSubmit={handleSubmit(fetchNewQuote)} autoComplete='off'>
        <ZigTypography component={'p'} sx={{ textAlign: 'center' }}>
          {t('buy-crypto-modal.specify-amount')}
        </ZigTypography>

        <Controller
          name={'currency'}
          control={control}
          render={({ field }) => (
            <ZigSelect
              id={'paybis-buy__input-currency'}
              label={t('buy-crypto-modal.fiat-currency')}
              placeholder={t('buy-crypto-modal.fiat-currency')}
              options={currencyOptions}
              disabled={isLoadingQuote || isLoadingDeposit}
              {...field}
            />
          )}
        />

        <Controller
          name={'method'}
          control={control}
          render={({ field }) => (
            <ZigSelect
              id={'paybis-buy__input-method'}
              label={t('buy-crypto-modal.fiat-method')}
              placeholder={t('buy-crypto-modal.fiat-method')}
              options={methodOptions}
              disabled={isLoadingQuote || isLoadingDeposit}
              {...field}
            />
          )}
        />

        <Controller
          name={'amount'}
          control={control}
          render={({ field }) => (
            <ZigInputAmount
              showMaxButton={false}
              id={'aybis-buy__input-amount'}
              label={t('buy-crypto-modal.amount')}
              wide={true}
              coin={'USDT'}
              min={MIN_INVESTMENT_FIREBLOCKS}
              extraInfo={{
                wrapExtraInfo: 3,
              }}
              disabled={isLoadingQuote || isLoadingDeposit}
              error={t(errors?.amount?.message, {
                minValue: MIN_INVESTMENT_FIREBLOCKS,
                minValueCoin: 'USDT',
              })}
              {...field}
            />
          )}
        />

        <ZigButton
          size={'large'}
          loading={isLoadingQuote || isLoadingIpInfo}
          disabled={!isValid || isLoadingDeposit}
          variant={activeQuote && isQuoteActionable ? 'outlined' : 'contained'}
          type={'submit'}
        >
          {t(
            activeQuote && activeQuote.amountTo === watch('amount')
              ? 'buy-crypto-modal.get-new-quote'
              : 'buy-crypto-modal.get-quote',
          )}
        </ZigButton>
      </ZigModalForm>

      {activeQuote && !isQuoteActionable && (
        <ZigTypography
          component={'p'}
          sx={{ textAlign: 'center', mt: 5 }}
          color={'neutral400'}
        >
          {t('buy-crypto-modal.quote-expired')}
        </ZigTypography>
      )}

      {lastLoadedQuote && isQuoteActionable && (
        <Box sx={{ mt: 2, opacity: !activeQuote ? 0.3 : 1 }}>
          <ZigTypography component={'p'} sx={{ mb: 1 }}>
            <Trans
              t={t}
              i18nKey={'buy-crypto-modal.amount-charged'}
              values={{
                amount: lastLoadedQuote.amountFrom,
                currency: lastLoadedQuote.currencyCodeFrom,
              }}
            >
              <ZigTypography
                color='neutral100'
                sx={{ fontWeight: 600, float: 'right' }}
              />
            </Trans>
          </ZigTypography>

          <ZigTypography component={'p'} sx={{ mb: 1 }}>
            <Trans
              t={t}
              i18nKey={'buy-crypto-modal.amount-received'}
              values={{
                amount: lastLoadedQuote.amountTo,
                coin: 'USDT',
              }}
            >
              <ZigTypography
                color='neutral100'
                sx={{ fontWeight: 600, float: 'right' }}
              />
            </Trans>
          </ZigTypography>

          <ZigButton
            fullWidth
            sx={{ mt: 2 }}
            size={'large'}
            disabled={!activeQuote}
            loading={isLoadingDeposit}
            onClick={submitPaymentRequest}
          >
            {t('buy-crypto-modal.accept-quote')}
          </ZigButton>
        </Box>
      )}
    </>
  );
}

export default PaybisBuyForm;
